import React, { useRef, useState } from 'react';
import { API_ENDPOINTS } from '../../config/apiRouting';
import { useAuth } from '../../contexts/AuthContext';
import {
  scanInvoice,
  fetchInvoiceMatch,
  InventoryApiError,
} from '../../services/inventoryApi';
import './InvoiceScanner.css';

interface InvoiceScannerProps {
  selectedStore: string;
  enterpriseId: string;
}

interface LineItem {
  description?: string;
  ingredientName?: string;
  ingredientId?: string;
  quantity?: number;
  unit?: string;
  unitPrice?: number;
  lineTotal?: number;
}

interface MatchResult {
  invoiceId?: string;
  status?: string;
  threeWayMatch?: {
    poMatched?: boolean;
    receivingMatched?: boolean;
    priceMatched?: boolean;
    discrepancies?: Array<{
      type: string;
      ingredientId?: string;
      description?: string;
      expected?: number;
      actual?: number;
    }>;
  };
  lineItems?: LineItem[];
  supplierName?: string;
  total?: number;
  invoiceDate?: string;
}

const InvoiceScanner: React.FC<InvoiceScannerProps> = ({
  selectedStore,
  enterpriseId,
}) => {
  const { getIdToken } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MatchResult | null>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setResult(null);

    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result as string);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      // Upload to S3 via the shared uploads microservice (base64 JSON body).
      const base64Data: string = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onerror = () => reject(r.error || new Error('File read failed'));
        r.onload = () => {
          const result = r.result as string;
          const comma = result.indexOf(',');
          resolve(comma >= 0 ? result.slice(comma + 1) : result);
        };
        r.readAsDataURL(file);
      });

      const uploadUrl = API_ENDPOINTS.uploadGeneral(enterpriseId);
      const token = (await getIdToken()) ||
        sessionStorage.getItem('idToken') || localStorage.getItem('idToken') ||
        sessionStorage.getItem('authToken') || localStorage.getItem('authToken');
      const uploadResp = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          base64Data,
        }),
      });
      const uploadJson = await uploadResp.json().catch(() => null);
      if (!uploadResp.ok || !uploadJson?.success) {
        throw new Error(uploadJson?.message || uploadJson?.error || `Upload failed: ${uploadResp.status}`);
      }
      const imageKey: string | undefined =
        uploadJson?.data?.key ||
        uploadJson?.data?.imageKey ||
        uploadJson?.key ||
        uploadJson?.imageKey;
      if (!imageKey) {
        throw new Error('Upload did not return an image key');
      }

      setUploading(false);
      setScanning(true);

      const scanResp: any = await scanInvoice(selectedStore, imageKey);
      const invoiceId =
        scanResp?.invoiceId || scanResp?.invoice?.invoiceId || scanResp?.id;

      let match: MatchResult = scanResp;
      if (invoiceId) {
        try {
          const matchResp = await fetchInvoiceMatch(selectedStore, invoiceId);
          match = { ...scanResp, ...matchResp, invoiceId };
        } catch {
          // fall back to scan response
        }
      }
      setResult(match);
    } catch (err) {
      const msg =
        err instanceof InventoryApiError ? err.message :
        err instanceof Error ? err.message : 'Scan failed';
      setError(msg);
    } finally {
      setUploading(false);
      setScanning(false);
    }
  };

  const discrepancies = result?.threeWayMatch?.discrepancies || [];

  return (
    <div className="invoice-scanner">
      <div className="invoice-scanner__header">
        <div>
          <h2>Invoice Scanner</h2>
          <p className="invoice-scanner__sub">
            Upload an invoice photo for OCR + three-way matching
          </p>
        </div>
        <button
          className="btn-modern btn-modern-accent"
          onClick={() => inputRef.current?.click()}
          disabled={uploading || scanning || !selectedStore}
        >
          {uploading ? 'Uploading...' : scanning ? 'Scanning...' : 'Upload Invoice'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,application/pdf"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </div>

      {error && <div className="invoice-scanner__error">{error}</div>}

      <div className="invoice-scanner__body">
        <div className="invoice-scanner__preview">
          {preview ? (
            <img src={preview} alt="Invoice preview" />
          ) : (
            <div className="invoice-scanner__placeholder">
              Select an invoice image to begin
            </div>
          )}
        </div>

        <div className="invoice-scanner__results">
          {!result && !uploading && !scanning && (
            <div className="invoice-scanner__state">
              Scan results will appear here.
            </div>
          )}

          {(uploading || scanning) && (
            <div className="invoice-scanner__state">
              {uploading ? 'Uploading to S3...' : 'Running Textract + match...'}
            </div>
          )}

          {result && (
            <>
              <div className="invoice-scanner__summary">
                <div>
                  <span className="invoice-scanner__label">Supplier</span>
                  <strong>{result.supplierName || '-'}</strong>
                </div>
                <div>
                  <span className="invoice-scanner__label">Invoice Date</span>
                  <strong>{result.invoiceDate || '-'}</strong>
                </div>
                <div>
                  <span className="invoice-scanner__label">Total</span>
                  <strong>
                    {result.total != null ? `$${result.total.toFixed(2)}` : '-'}
                  </strong>
                </div>
                <div>
                  <span className="invoice-scanner__label">Status</span>
                  <strong>{result.status || '-'}</strong>
                </div>
              </div>

              {result.threeWayMatch && (
                <div className="invoice-scanner__match">
                  <h3>Three-Way Match</h3>
                  <div className="invoice-scanner__match-flags">
                    <span
                      className={`invoice-scanner__flag ${
                        result.threeWayMatch.poMatched ? 'ok' : 'bad'
                      }`}
                    >
                      PO {result.threeWayMatch.poMatched ? 'matched' : 'mismatch'}
                    </span>
                    <span
                      className={`invoice-scanner__flag ${
                        result.threeWayMatch.receivingMatched ? 'ok' : 'bad'
                      }`}
                    >
                      Receiving{' '}
                      {result.threeWayMatch.receivingMatched ? 'matched' : 'mismatch'}
                    </span>
                    <span
                      className={`invoice-scanner__flag ${
                        result.threeWayMatch.priceMatched ? 'ok' : 'bad'
                      }`}
                    >
                      Price {result.threeWayMatch.priceMatched ? 'matched' : 'mismatch'}
                    </span>
                  </div>

                  {discrepancies.length > 0 && (
                    <div className="invoice-scanner__discrepancies">
                      <h4>Discrepancies</h4>
                      <ul>
                        {discrepancies.map((d, i) => (
                          <li key={i}>
                            <strong>{d.type}</strong>
                            {d.description && ` - ${d.description}`}
                            {d.expected != null && d.actual != null && (
                              <span>
                                {' '}(expected {d.expected}, actual {d.actual})
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {result.lineItems && result.lineItems.length > 0 && (
                <div className="invoice-scanner__lines">
                  <h3>Line Items ({result.lineItems.length})</h3>
                  <table>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Qty</th>
                        <th>Unit</th>
                        <th>Unit Price</th>
                        <th>Line Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.lineItems.map((li, i) => (
                        <tr key={i}>
                          <td>{li.ingredientName || li.description || '-'}</td>
                          <td>{li.quantity ?? '-'}</td>
                          <td>{li.unit || '-'}</td>
                          <td>
                            {li.unitPrice != null ? `$${li.unitPrice.toFixed(2)}` : '-'}
                          </td>
                          <td>
                            {li.lineTotal != null ? `$${li.lineTotal.toFixed(2)}` : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceScanner;
